import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, interval, Subject, takeUntil, takeWhile } from 'rxjs';
import { CdkTrapFocus, LiveAnnouncer } from '@angular/cdk/a11y';
import type { BaseMarker } from '@go-modules/interfaces/markers/marker';
import { TagResource } from '@go-modules/interfaces/tag-resource';
import { ImprovedCommentResponse, LlmTaskService } from '@go-modules/services/llm-task/llm-task.service';
import { FormatTimePipe } from '@go-modules/pipes/format-time/format-time.pipe';

@Component({
	selector: 'ai-comment-assistant-menu',
	templateUrl: './ai-comment-assistant-menu.component.html',
	styleUrls: ['./ai-comment-assistant-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [FormatTimePipe],
	standalone: false
})
export class AICommentAssistantMenuComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() public set commentText (value: string) {
		this.plainCommentText = this.stripHtml(value);
	}
	@Input() public sessionId: number;
	@Input() public time: number;
	@Input() public tagResources: TagResource[] = [];
	@Output() public onClose = new EventEmitter<void>();
	@Output() public onApplyImprovedComment = new EventEmitter<ImprovedCommentResponse>();

	public tagsList: BaseMarker[] = [];
	public generating$ = new BehaviorSubject<boolean>(false);
	public hasErrors$ = new BehaviorSubject<boolean>(true);
	public isRateLimited$ = new BehaviorSubject<boolean>(false);
	public retryAfter$ = new BehaviorSubject<number>(0);
	public improvedComment = '';
	public plainCommentText = '';
	public llmTaskId: number;
	protected llmTaskService: LlmTaskService;
	private readonly componentDestroyed$ = new Subject<void>();
	@ViewChild(CdkTrapFocus) private focusTrapDirective: CdkTrapFocus;

	constructor (
		llmTaskService: LlmTaskService,
		private formatTimePipe: FormatTimePipe,
		private liveAnnouncer: LiveAnnouncer
	) {
		this.llmTaskService = llmTaskService;
	}

	public ngAfterViewInit (): void {
		this.focusTrapDirective.focusTrap.focusFirstTabbableElement();
	}

	public ngOnInit (): void {
		this.tagsList = this.tagResources.map((tagResource: TagResource) => tagResource.item);
		this.fetchImprovedComment();
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$.next();
		this.componentDestroyed$.complete();
	}

	public fetchImprovedComment () {
		this.generating$.next(true);
		this.hasErrors$.next(false);
		this.isRateLimited$.next(false);
		this.retryAfter$.next(0);

		const formattedTime = this.formatTimePipe.transform(this.time, 'timer', true);
		this.llmTaskService.getImprovedComment(this.plainCommentText, this.tagsList, this.sessionId, formattedTime)
			.pipe(takeUntil(this.componentDestroyed$))
			.subscribe({
				next: (res: ImprovedCommentResponse) => {
					this.improvedComment = res.comment;
					this.llmTaskId = res.llm_task_id;
					this.liveAnnouncer.announce(this.improvedComment, 'polite');
				},
				error: (err) => {
					this.generating$.next(false);
					this.hasErrors$.next(true);
					if (err.error?.retry_after) {
						this.isRateLimited$.next(true);
						this.retryAfter$.next(err.error.retry_after);
						interval(1000)
							.pipe(
								takeUntil(this.componentDestroyed$),
								takeWhile(() => this.retryAfter$.value > 0)
							)
							.subscribe(() => {
								this.retryAfter$.next(this.retryAfter$.value - 1);
								// try again automatically when the rate limit is over
								if (this.retryAfter$.value <= 0) {
									this.fetchImprovedComment();
								}
							});
					} else {
						this.isRateLimited$.next(false);
					}
				},
				complete: () => this.generating$.next(false)
			});
	}

	public apply () {
		this.onApplyImprovedComment.emit({ comment: this.improvedComment, llm_task_id: this.llmTaskId });
		this.onClose.emit();
	}

	public onMenuKeydown (event: KeyboardEvent) {
		if (event.key === 'Tab') {
			event.stopPropagation();
		}
	}

	private stripHtml (html: string): string {
		const div = document.createElement('div');
		div.innerHTML = html;
		return div.textContent;
	}
}
